// A support view uses the operator's existing login cookie plus a short-lived,
// tab-scoped token. The token is never put in localStorage or sent in a URL
// request; the fragment is removed before the app starts fetching data.
const STORAGE_KEY = 'dealton_support_view_token';
const HEADER = 'X-Dealton-View-As';
let token = '';

export function isSupportView(): boolean { return !!token; }

export function initializeSupportView(): void {
  if (typeof window === 'undefined') return;
  const fragment = window.location.hash;
  if (fragment.startsWith('#support-view=')) {
    const incoming = fragment.slice('#support-view='.length);
    if (/^[A-Za-z0-9_-]{40,80}$/.test(incoming)) {
      sessionStorage.setItem(STORAGE_KEY, incoming);
    }
    history.replaceState(history.state, '', window.location.pathname + window.location.search);
  }
  token = sessionStorage.getItem(STORAGE_KEY) || '';
  if (!token) return;
  document.documentElement.classList.add('support-view-active');

  // Every app API call, including FormData calls outside lib/api/client.ts,
  // carries the support identity. Requests to third-party origins stay intact.
  const originalFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const rawUrl = input instanceof Request ? input.url : String(input);
    const url = new URL(rawUrl, window.location.href);
    if (url.origin !== window.location.origin || !url.pathname.startsWith('/api/')) {
      return originalFetch(input, init);
    }
    const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : undefined));
    headers.set(HEADER, token);
    if (input instanceof Request) return originalFetch(new Request(input, { ...init, headers }));
    return originalFetch(input, { ...init, headers });
  };
}

export async function leaveSupportView(): Promise<void> {
  if (!token) return;
  try {
    await fetch('/api/super-admin/view-as/end', { method: 'POST', credentials: 'include' });
  } finally {
    sessionStorage.removeItem(STORAGE_KEY);
    token = '';
    document.documentElement.classList.remove('support-view-active');
    window.close();
    // A restored tab or browser that refuses window.close returns to the
    // operator's own session, without switching the main tab or its cookie.
    window.location.replace('/super-admin');
  }
}

export async function downloadSupportFile(event: MouseEvent, url: string,
                                          filename: string): Promise<void> {
  if (!isSupportView()) return; // Keep the normal browser download path.
  event.preventDefault();
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) throw new Error(`Download unavailable (${response.status})`);
  const objectUrl = URL.createObjectURL(await response.blob());
  try {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }
}
